import {
  FeeOffsetApplicationsUpsertMutationBody,
  useFeeOffsetApplicationsUpsert,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import { FeeOffsetApplicationFormReadRowData } from "gen/model/feeOffsetApplicationFormReadRowData";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { FundFamilyUseAllocationTableColumnDataFeeOffset } from "gen/model/fundFamilyUseAllocationTableColumnDataFeeOffset";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { FundFamilyUseAllocationTableRowFunds } from "gen/model/fundFamilyUseAllocationTableRowFunds";
import { get } from "lodash-es";
import { useCallback } from "react";
import { FundFamilyUseAllocationTableGetErrorForCellFn } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { FundFamilyUseAllocationTableAmountColHeaderComponentParams } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableTypes";
import { isFundFamilyDef } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import { getApiErrorFromResponse } from "shared/utils/ErrorUtils";

// TODO: Update backend to use allocation table data model,
//  then remove this file.

export const feeOffsetApplicationsToRows = (
  feeApplications: FeeOffsetApplicationFormReadRowData[],
  fundList: FundEntityReadData[],
): FundFamilyUseAllocationTableRow[] =>
  feeApplications.map(
    (a): FundFamilyUseAllocationTableRow => ({
      id: a.fee_offsettable_expense_id,
      category_id: "offset",
      is_read_only: a.state === FundFamilyEventStateMachineFieldsEnum.completed,
      is_selected:
        a.state === FundFamilyEventStateMachineFieldsEnum.completed ||
        a.state === FundFamilyEventStateMachineFieldsEnum.draft,
      fund_family: {
        offset: {
          amount: a.pro_rata_application_amount || "",
        },
      },
      funds: fundList
        .filter((f) => a.fund_entities_affected_ids.includes(f.id))
        .reduce<FundFamilyUseAllocationTableRowFunds>(
          (acc, f) => ({
            ...acc,
            [f.id]: {
              offset: {
                amount: a.fund_id_to_fund_application_amount[f.id] || "",
              },
            },
          }),
          {},
        ),
      is_auto_allocate: a.is_pro_rata,
      column_data: { type: "fee_offset", ...a },
    }),
  );

export const feeOffsetRowsToRequestData = (
  rows: FundFamilyUseAllocationTableRow[],
  includeCalculatedAmounts = false,
  includePosted = false,
): FeeOffsetApplicationsUpsertMutationBody => ({
  fee_offset_applications: rows
    .filter((row) =>
      includePosted ? row.is_selected : row.is_selected && !row.is_read_only,
    )
    .map((row) => ({
      is_pro_rata: row.is_auto_allocate,
      pro_rata_application_amount:
        row.is_auto_allocate || includeCalculatedAmounts
          ? row.fund_family["offset"].amount
          : null,
      fund_id_to_fund_application_amount:
        !row.is_auto_allocate || includeCalculatedAmounts
          ? Object.entries(row.funds).reduce<{
              [fundId: number]: string;
            }>(
              (acc, [fundId, fundAmounts]) => ({
                ...acc,
                [fundId]: fundAmounts["offset"].amount,
              }),
              {},
            )
          : {},
      fee_offsettable_expense_id: (
        row.column_data as
          | FundFamilyUseAllocationTableColumnDataFeeOffset
          | undefined
      )?.fee_offsettable_expense_id as string,
    })),
});

export const useFeeOffsetGetErrorForCellFn = (
  upsertError: unknown,
  upsertVariables:
    | undefined
    | ReturnType<typeof useFeeOffsetApplicationsUpsert>["variables"],
) =>
  useCallback<FundFamilyUseAllocationTableGetErrorForCellFn>(
    (params) => {
      const colFieldOrId =
        params.column?.getColDef().field || params.column?.getColId();
      if (!colFieldOrId) {
        return undefined;
      }

      const apiErrorData = getApiErrorFromResponse(upsertError);
      if (!apiErrorData) {
        return undefined;
      }

      const originalIndex =
        upsertVariables?.data.fee_offset_applications.findIndex(
          (it) => it.fee_offsettable_expense_id === params.node.id,
        ) ?? -1;
      if (originalIndex === -1) {
        return undefined;
      }

      const headerParams:
        | FundFamilyUseAllocationTableAmountColHeaderComponentParams
        | undefined = params.column?.getColDef().headerComponentParams;
      if (!headerParams?.fundFamilyOrFundDef) {
        return undefined;
      }

      const errorPath = isFundFamilyDef(headerParams.fundFamilyOrFundDef)
        ? `fee_offset_applications.${originalIndex}.pro_rata_application_amount`
        : `fee_offset_applications.${originalIndex}.fund_id_to_fund_application_amount.${headerParams.fundFamilyOrFundDef.fund_id}`;
      const errors: string[] | undefined = get(apiErrorData.data, errorPath);
      return errors?.join(",");
    },
    [upsertError, upsertVariables],
  );
